import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { leaveAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Plus, Loader2, Trash2, Edit, Upload, Download, FileSpreadsheet } from 'lucide-react';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const Holidays = () => {
  const { user } = useAuth();
  const { dialogProps, confirm } = useConfirmation();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: '',
    is_optional: false,
  });
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const handleDownloadTemplate = () => {
    const headers = ['Date', 'Holiday Name', 'Description', 'Type'];
    const sampleRow = [`${selectedYear}-01-01`, 'New Year', 'New Year Celebration', 'Mandatory'];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holiday_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls|csv)$/)) {
      toast.error('Please upload an Excel or CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const response = await leaveAPI.uploadHolidays(formData);
      toast.success(response.data.message || 'Holidays uploaded successfully');
      await fetchHolidays();
      setIsUploadOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload holidays');
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await leaveAPI.getHolidays(selectedYear);
      setHolidays(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError(error.response.data.detail);
      } else {
        toast.error('Failed to fetch holidays');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHoliday = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await leaveAPI.createHoliday({
        ...formData,
        company_id: user.company_id,
      });
      toast.success('Holiday added');
      setIsCreateOpen(false);
      setFormData({ name: '', date: '', description: '', is_optional: false });
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to add holiday');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (holidayId) => {
    const confirmed = await confirm({
      title: 'Delete Holiday',
      description: 'Are you sure you want to delete this holiday? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await leaveAPI.deleteHoliday(holidayId);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ModuleDisabled moduleName="leave" />;
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Holidays</h1>
          <p className="text-muted-foreground">Manage company holiday calendar</p>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border rounded-md"
          >
            {[2023, 2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <Button variant="outline" onClick={() => setIsUploadOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Bulk Upload
          </Button>

          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Bulk Upload Holidays</DialogTitle>
                <DialogDescription>
                  Upload holiday schedule using the provided template.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-6 py-4">
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900/20">
                      <FileSpreadsheet className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Holiday Template.csv</p>
                      <p className="text-xs text-muted-foreground">Required format for upload</p>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleDownloadTemplate}>
                    <Download className="mr-2 h-3 w-3" />
                    Download
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Upload Schedule File</Label>
                  <div className="flex items-center justify-center w-full">
                    <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-4 text-muted-foreground" />
                        <p className="mb-2 text-sm text-muted-foreground"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                        <p className="text-xs text-muted-foreground">CSV, XLSX, XLS (MAX. 5MB)</p>
                      </div>
                      <input id="dropzone-file" type="file" className="hidden" accept=".csv, .xlsx, .xls" onChange={handleFileUpload} />
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-holiday-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Holiday
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Holiday</DialogTitle>
                <DialogDescription>
                  Add a new holiday to the company calendar
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateHoliday}>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Holiday Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="holiday-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="holiday-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_optional"
                      checked={formData.is_optional}
                      onChange={(e) => setFormData({ ...formData, is_optional: e.target.checked })}
                    />
                    <Label htmlFor="is_optional">Optional Holiday</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createLoading}>
                    {createLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Add Holiday
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Holiday Calendar {selectedYear}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Day</TableHead>
                <TableHead>Holiday Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidays.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No holidays found for {selectedYear}
                  </TableCell>
                </TableRow>
              ) : (
                holidays.map((holiday) => {
                  const date = new Date(holiday.date);
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                  return (
                    <TableRow key={holiday.id}>
                      <TableCell>{date.toLocaleDateString()}</TableCell>
                      <TableCell>{dayName}</TableCell>
                      <TableCell className="font-medium">{holiday.name}</TableCell>
                      <TableCell>{holiday.description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={holiday.is_optional ? 'secondary' : 'default'}>
                          {holiday.is_optional ? 'Optional' : 'Mandatory'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleDelete(holiday.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfirmationDialog {...dialogProps} />
    </div>
  );
};

export default Holidays;
